import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FileArchive,
  FileText,
  Hash,
  Headphones,
  HelpCircle,
  Image as ImageIcon,
  KeyRound,
  Layers,
  Link2,
  Loader2,
  Lock,
  MessageSquareText,
  MonitorSmartphone,
  PackageCheck,
  ShieldCheck,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const fmtSize = (b) =>
  !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

const fmtValidated = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    const date = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "long", year: "numeric" }).format(d);
    const time = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(d);
    return `${date} à ${time}`;
  } catch (e) {
    return "";
  }
};

function DeliveryImage({ orderId, itemId, className = "", onClick }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let active = true;
    let url = "";
    api
      .get(`/my/delivery/${orderId}/file/${itemId}`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        if (active) setSrc(url);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [orderId, itemId]);
  if (!src) {
    return (
      <div className={`flex h-40 items-center justify-center rounded-xl bg-slate-100 ${className}`}>
        <ImageIcon className="h-6 w-6 text-slate-300" />
      </div>
    );
  }
  return <img src={src} alt="" className={className} onClick={onClick} />;
}

function Crumb({ to, children }) {
  return (
    <>
      <Link to={to} className="shrink-0 text-slate-500 transition-colors duration-200 hover:text-brand">
        {children}
      </Link>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
    </>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <p className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <span className="text-slate-500">{label} :</span>
      <span className="min-w-0 break-words font-bold text-ink">{value}</span>
    </p>
  );
}

export default function AccountDelivery() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/my/delivery/${id}`);
      setData(res);
    } catch (err) {
      setError(apiError(err, "Commande introuvable"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copié !");
    } catch (e) {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const openLaunch = (item) => navigate(`/compte/commandes/${id}/telechargement/${item.id}`);

  if (error) {
    return (
      <div className="container-it py-16">
        <div className="mx-auto max-w-lg rounded-2xl border border-line bg-white p-10 text-center">
          <p className="text-sm font-bold text-ink">{error}</p>
          <Link to="/compte/commandes" className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
            Retour à mes commandes
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-32 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const product = data.product || {};
  const files = data.items.filter((i) => i.type === "file");
  const others = data.items.filter((i) => i.type !== "file");
  const locked = data.locked;

  return (
    <div className="container-it space-y-5 py-6 lg:py-8" data-testid="account-delivery-page">
      {/* ---- Fil d'Ariane ---- */}
      <nav className="flex flex-wrap items-center gap-1.5 text-xs font-semibold" data-testid="delivery-breadcrumb">
        <Crumb to="/">Accueil</Crumb>
        <Crumb to="/compte">Mon compte</Crumb>
        <Crumb to="/compte/commandes">Mes commandes</Crumb>
        <Crumb to={`/compte/commandes/${id}`}>Commande #{data.order_number}</Crumb>
        <span className="shrink-0 font-bold text-ink">Téléchargements</span>
      </nav>

      {/* ---- Bandeau de confirmation ---- */}
      {locked ? (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:items-center" data-testid="delivery-confirm-banner">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <Lock className="h-5 w-5 text-amber-600" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-amber-800" data-testid="delivery-status-badge">En attente de validation</p>
            <p className="text-xs leading-relaxed text-amber-700">
              Votre commande n'a pas encore été validée par l'administrateur. Les téléchargements seront activés automatiquement après validation.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:items-center" data-testid="delivery-confirm-banner">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-emerald-800" data-testid="delivery-status-badge">Commande confirmée</p>
            <p className="text-xs leading-relaxed text-emerald-700">
              Votre commande a été validée par l'administrateur. Vous pouvez maintenant accéder à vos fichiers et contenus.
            </p>
          </div>
        </div>
      )}

      {/* ---- Message personnalisé de la boutique ---- */}
      {!locked && data.custom_message && (
        <div className="flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand-light/50 p-4" data-testid="delivery-custom-message">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
            <MessageSquareText className="h-5 w-5 text-brand" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-ink">Message de la boutique</p>
            <p className="whitespace-pre-line text-xs leading-relaxed text-slate-600">{data.custom_message}</p>
          </div>
        </div>
      )}

      {/* ---- Carte produit + statut ---- */}
      <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="delivery-product-card">
        <div className="grid gap-6 lg:grid-cols-[auto_1fr_280px]">
          <div className="mx-auto w-40 shrink-0 sm:w-44 lg:mx-0">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full rounded-xl border border-line object-cover shadow-sm"
                data-testid="delivery-product-image"
              />
            ) : (
              <div className="flex h-52 w-full items-center justify-center rounded-xl bg-slate-100">
                <PackageCheck className="h-8 w-8 text-slate-300" />
              </div>
            )}
          </div>

          <div className="min-w-0">
            <h1 className="font-display text-xl font-extrabold text-ink sm:text-2xl" data-testid="delivery-product-title">
              {product.name || data.products.map((p) => p.name).join(", ")}
            </h1>
            <div className="mt-4 space-y-2.5">
              <InfoRow icon={Building2} label="Éditeur" value={product.editor} />
              <InfoRow icon={MonitorSmartphone} label="Plateforme" value={product.platform} />
              <InfoRow icon={Layers} label="Catégorie" value={product.category} />
              <InfoRow icon={ShieldCheck} label="Licence" value={product.license_type} />
              {!locked && data.total_size > 0 && <InfoRow icon={FileArchive} label="Taille totale" value={fmtSize(data.total_size)} />}
              {!locked && <InfoRow icon={FileText} label="Nombre de fichiers" value={String(data.files_count)} />}
              <InfoRow icon={CheckCircle2} label="Date d'achat" value={formatDate(data.created_at)} />
              <InfoRow icon={Hash} label="Commande" value={`#${data.order_number}`} />
            </div>
          </div>

          <div className="h-fit rounded-2xl border border-line bg-slate-50/60 p-4" data-testid="delivery-status-card">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <PackageCheck className="h-4 w-4 text-slate-400" /> Statut
              </p>
              {locked ? (
                <span className="rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-700">Verrouillée</span>
              ) : (
                <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700">Validée</span>
              )}
            </div>
            {!locked && data.validated_at && (
              <p className="mt-3 text-xs text-slate-500">
                Achat validé le
                <span className="mt-0.5 block text-sm font-bold text-ink" data-testid="delivery-validated-at">{fmtValidated(data.validated_at)}</span>
              </p>
            )}
            {locked && (
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                En attente de validation par l'administrateur.
              </p>
            )}
            <Link
              to={`/compte/commandes/${id}`}
              data-testid="delivery-view-order-btn"
              className="mt-4 flex items-center justify-center rounded-full border border-brand/30 px-4 py-2.5 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
            >
              Voir le détail de la commande
            </Link>
          </div>
        </div>
      </div>

      {locked ? (
        /* ---- État verrouillé ---- */
        <div className="rounded-2xl border border-line bg-white p-8 text-center sm:p-10" data-testid="delivery-locked-panel">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <Lock className="h-6 w-6 text-slate-400" />
          </span>
          <p className="mt-4 text-sm font-bold text-ink">Accès verrouillé</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
            Vos fichiers, liens et clés seront activés dès que l'administrateur aura validé votre commande. Vous recevrez une notification.
          </p>
          <button
            type="button"
            disabled
            data-testid="delivery-locked-download-btn"
            className="mx-auto mt-5 flex cursor-not-allowed items-center gap-2 rounded-full bg-slate-200 px-6 py-3 text-xs font-bold text-slate-400"
          >
            <Lock className="h-3.5 w-3.5" /> Télécharger
          </button>
          <p className="mt-2 text-[11px] font-semibold text-slate-400">Accès disponible après validation</p>
        </div>
      ) : (
        <>
          {files.length === 0 && others.length === 0 && (
            <div className="rounded-2xl border border-line bg-white p-8 text-center" data-testid="delivery-empty-panel">
              <p className="text-sm font-bold text-ink">Aucun contenu disponible pour le moment</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">
                Le contenu de votre livraison est en cours de mise à jour. Revenez un peu plus tard.
              </p>
            </div>
          )}
          {/* ---- Vos fichiers à télécharger ---- */}
          {files.length > 0 && (
            <section className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="delivery-files-section">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-ink">
                <Download className="h-4 w-4 text-brand" /> Vos fichiers à télécharger ({files.length})
              </h2>
              <p className="mt-1 text-xs text-slate-500">Cliquez sur un fichier ci-dessous pour lancer le téléchargement.</p>
              <div className="mt-4 space-y-3">
                {files.map((item) => {
                  const exhausted = item.download_limit > 0 && item.downloads_used >= item.download_limit;
                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openLaunch(item)}
                      onKeyDown={(e) => e.key === "Enter" && openLaunch(item)}
                      className="cursor-pointer rounded-2xl border border-line p-4 transition-colors duration-200 hover:border-brand/40 hover:bg-brand-light/40"
                      data-testid={`delivery-file-${item.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light">
                            <FileArchive className="h-5 w-5 text-brand" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-ink">{item.title}</p>
                            <p className="truncate text-xs text-slate-500">
                              {item.file_name}
                              {item.file_size ? ` · ${fmtSize(item.file_size)}` : ""}
                            </p>
                            {item.description && <p className="truncate text-[11px] text-slate-400">{item.description}</p>}
                          </div>
                        </div>
                        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
                          {item.is_expired ? (
                            <span className="flex items-center gap-1.5 text-[11px] font-bold text-rose-600">
                              <Lock className="h-3.5 w-3.5" /> Expiré
                            </span>
                          ) : exhausted ? (
                            <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-600">
                              <Lock className="h-3.5 w-3.5" /> Limite atteinte
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Prêt à télécharger
                            </span>
                          )}
                          {item.is_expired || exhausted ? (
                            <Link
                              to={`/compte/commandes/${id}/telechargement/${item.id}`}
                              data-testid={`delivery-download-${item.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs font-bold text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand"
                            >
                              Voir le détail
                            </Link>
                          ) : (
                            <Link
                              to={`/compte/commandes/${id}/telechargement/${item.id}`}
                              data-testid={`delivery-download-${item.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
                            >
                              <Download className="h-3.5 w-3.5" />
                              Télécharger
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ---- Liens, clés, textes, images ---- */}
          {others.length > 0 && (
            <section className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="delivery-others-section">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-ink">
                <KeyRound className="h-4 w-4 text-brand" /> Vos liens, clés et instructions ({others.length})
              </h2>
              <div className="mt-4 space-y-3">
                {others.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-line p-4" data-testid={`delivery-item-${item.id}`}>
                    {item.type === "link" && (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light">
                            <Link2 className="h-5 w-5 text-brand" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-ink">{item.title}</p>
                            <p className="truncate text-xs font-semibold text-brand">{item.url}</p>
                            {item.description && <p className="text-[11px] text-slate-400">{item.description}</p>}
                          </div>
                        </div>
                        {item.is_expired ? (
                          <span className="flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] font-bold text-rose-600">
                            <Lock className="h-3 w-3" /> Lien expiré
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`delivery-copy-link-${item.id}`}
                              onClick={() => copy(item.url)}
                              className="flex items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
                            >
                              <Copy className="h-3.5 w-3.5" /> Copier
                            </button>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`delivery-open-link-${item.id}`}
                              className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> Ouvrir le lien
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {item.type === "key" && (
                      <div>
                        <p className="flex items-center gap-2 text-sm font-bold text-ink">
                          <KeyRound className="h-4 w-4 text-brand" /> {item.title}
                        </p>
                        {item.description && <p className="mt-0.5 text-[11px] text-slate-400">{item.description}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <code className="min-w-0 flex-1 break-all rounded-xl border border-line bg-slate-50 px-4 py-3 font-mono text-sm font-bold tracking-wide text-ink" data-testid={`delivery-key-value-${item.id}`}>
                            {item.value}
                          </code>
                          <button
                            type="button"
                            data-testid={`delivery-copy-key-${item.id}`}
                            onClick={() => copy(item.value)}
                            className="flex items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
                          >
                            <Copy className="h-3.5 w-3.5" /> Copier
                          </button>
                        </div>
                      </div>
                    )}

                    {item.type === "text" && (
                      <div>
                        <p className="flex items-center gap-2 text-sm font-bold text-ink">
                          <FileText className="h-4 w-4 text-brand" /> {item.title}
                        </p>
                        <div className="mt-2 whitespace-pre-wrap rounded-xl border border-line bg-slate-50 p-4 text-sm leading-relaxed text-ink">
                          {item.content}
                        </div>
                      </div>
                    )}

                    {item.type === "image" && (
                      <div>
                        <p className="flex items-center gap-2 text-sm font-bold text-ink">
                          <ImageIcon className="h-4 w-4 text-brand" /> {item.title}
                        </p>
                        {item.description && <p className="mt-0.5 text-[11px] text-slate-400">{item.description}</p>}
                        <DeliveryImage
                          orderId={id}
                          itemId={item.id}
                          className="mt-2 max-h-72 w-full cursor-zoom-in rounded-xl border border-line object-contain"
                          onClick={() => setZoom(item)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <p className="flex items-center gap-2 rounded-xl bg-brand-light px-4 py-3 text-[11px] font-semibold text-brand">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            Cette page reste accessible à tout moment depuis « Mes commandes → Détails → Télécharger ».
          </p>
        </>
      )}

      {/* ---- Support ---- */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white p-4 sm:p-5" data-testid="delivery-support-card">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light">
            <HelpCircle className="h-5 w-5 text-brand" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-ink">Besoin d'aide ou un problème ?</p>
            <p className="text-xs text-slate-500">Notre équipe est disponible pour vous aider.</p>
          </div>
        </div>
        <Link
          to="/support"
          data-testid="delivery-contact-support-btn"
          className="flex items-center gap-2 rounded-full border border-brand/30 px-5 py-2.5 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
        >
          <Headphones className="h-3.5 w-3.5" /> Contacter le support
        </Link>
      </div>

      <Dialog open={Boolean(zoom)} onOpenChange={(v) => !v && setZoom(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-4xl p-2">
          {zoom && <DeliveryImage orderId={id} itemId={zoom.id} className="max-h-[80vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
